import * as _ from "lodash";
import * as superagent from "superagent";
/**
 * Front end API access for the Bigscreen api.
 *
 * WARNING: don't edit the .js version of this file directly. Edit the .ts version and then run `tsc` to generate the .js version.
 */
const AuthenticationErrorCode = {
    Unknown: 0,
    BadAPIKey: 1,
    MissingAuthenticationHeaders: 2,
    RefreshTokenNeedsRenewal: 3,
    AccessTokenNeedsRenewal: 4,
    RenewalNonceExpired: 5,
    TokenParserError: 6,
    InvalidLoginCredentials: 7,
    BannedAccount: 8,
    BannedIP: 9,
    BannedDevice: 10,
    RequiresVerifiedEmailAddress: 11
};
export var BigApiAuthenticationGuard;
(function (BigApiAuthenticationGuard) {
    BigApiAuthenticationGuard[BigApiAuthenticationGuard["SkipAuthentication"] = 0] = "SkipAuthentication";
    BigApiAuthenticationGuard[BigApiAuthenticationGuard["AuthenticationOptional"] = 1] = "AuthenticationOptional";
    BigApiAuthenticationGuard[BigApiAuthenticationGuard["AuthenticationRequired"] = 2] = "AuthenticationRequired";
})(BigApiAuthenticationGuard || (BigApiAuthenticationGuard = {}));
export class BigApiError extends Error {
    code;
    status;
    errors;
    details;
    static getApiError(err, defaultMessage = "API operation failed! Please try again later.") {
        console.error(err);
        const error = new BigApiError();
        if (err.errno && err.errno === "ENOTFOUND") {
            error.message = "Couldn't connect to the Bigscreen servers.";
        }
        else if (err.timeout) {
            error.message = "Time out. Try again later.";
        }
        if (err.response) {
            if (err.response.status) {
                error.status = err.response.status;
            }
            if (err.response.body) {
                console.log(err.response.body);
                if (err.response.body.errors) {
                    error.errors = err.response.body.errors;
                }
                if (err.response.body.code) {
                    error.code = err.response.body.code;
                }
                if (err.response.body.message) {
                    error.message = err.response.body.message;
                }
                if (err.response.body.msg) {
                    error.message = err.response.body.msg;
                }
                if (err.response.body.details) {
                    console.log('[BigApiError] Found details in response:', err.response.body.details);
                    error.details = err.response.body.details;
                }
            }
            else {
                error.message = defaultMessage;
            }
        }
        else {
            error.message = defaultMessage;
        }
        return error;
    }
}
export class BigApi {
    static async init(config) {
        BigApi.config = config;
        try {
            const accessTokenResult = await BigApi.checkAccessTokenStatus();
            if (accessTokenResult) {
                const accountProfile = await BigApi.fetchLocalAccountProfile();
                return accountProfile;
            }
            return null;
        }
        catch (e) {
            console.error(e);
        }
    }
    static REFRESH_TOKEN_KEY = "x-refresh-token";
    static ACCESS_TOKEN_KEY = "x-access-token";
    static USE_COOKIES_FOR_TOKENS = true;
    static setCookie(name, value, daysToExpire) {
        const expirationDate = new Date();
        expirationDate.setDate(expirationDate.getDate() + daysToExpire);
        const cookieValue = encodeURIComponent(name) + '=' + encodeURIComponent(value) + '; expires=' + expirationDate.toUTCString() + '; path=/';
        document.cookie = cookieValue;
    }
    static getCookie(name) {
        const cookieName = encodeURIComponent(name) + '=';
        const cookieArray = document.cookie.split(';');
        for (let i = 0; i < cookieArray.length; i++) {
            let cookie = cookieArray[i];
            while (cookie.charAt(0) === ' ') {
                cookie = cookie.substring(1);
            }
            if (cookie.indexOf(cookieName) === 0) {
                return decodeURIComponent(cookie.substring(cookieName.length, cookie.length));
            }
        }
        return null;
    }
    static deleteCookie(name) {
        document.cookie = encodeURIComponent(name) + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    }
    /**
     * TODO: handle different browser storage mechanisms (e.g. cookies).
     */
    static setRefreshToken(refreshToken) {
        if (BigApi.USE_COOKIES_FOR_TOKENS) {
            BigApi.setCookie(BigApi.REFRESH_TOKEN_KEY, refreshToken, 14);
        }
        else {
            localStorage.setItem(BigApi.REFRESH_TOKEN_KEY, refreshToken);
        }
    }
    static setAccessToken(accessToken) {
        if (BigApi.USE_COOKIES_FOR_TOKENS) {
            BigApi.setCookie(BigApi.ACCESS_TOKEN_KEY, accessToken, 14);
        }
        else {
            localStorage.setItem(BigApi.ACCESS_TOKEN_KEY, accessToken);
        }
    }
    static getRefreshToken() {
        if (BigApi.USE_COOKIES_FOR_TOKENS) {
            return BigApi.getCookie(BigApi.REFRESH_TOKEN_KEY);
        }
        return localStorage.getItem(BigApi.REFRESH_TOKEN_KEY);
    }
    static getAccessToken() {
        if (BigApi.USE_COOKIES_FOR_TOKENS) {
            return BigApi.getCookie(BigApi.ACCESS_TOKEN_KEY);
        }
        return localStorage.getItem(BigApi.ACCESS_TOKEN_KEY);
    }
    static deleteTokens() {
        if (BigApi.USE_COOKIES_FOR_TOKENS) {
            BigApi.deleteCookie(BigApi.REFRESH_TOKEN_KEY);
            BigApi.deleteCookie(BigApi.ACCESS_TOKEN_KEY);
        }
        else {
            localStorage.removeItem(BigApi.REFRESH_TOKEN_KEY);
            localStorage.removeItem(BigApi.ACCESS_TOKEN_KEY);
        }
    }
    static async getSystemInfo() {
        let ip = "unknown";
        try {
            const res = await superagent.get(`${BigApi.config.apiServerUrl}/auth/whoami`)
                .set("Authorization", `Bearer ${BigApi.config.apiKey}`);
            ip = res.body.ip;
        }
        catch (e) {
            console.error(e);
        }
        return {
            deviceName: window.navigator.userAgent,
            deviceUniqueIdentifier: `web_${ip}`
        };
    }
    static getIp(request) {
        return request.headers["x-forwarded-for"] || request.ip;
    }
    static async login(payload) {
        try {
            const res = await superagent.post(`${BigApi.config.apiServerUrl}/auth/login`)
                .set("Authorization", `Bearer ${BigApi.config.apiKey}`)
                .timeout(10000)
                .accept('json')
                .send(payload);
            const refreshToken = res.headers['x-refresh-token'];
            const accessToken = res.headers['x-access-token'];
            BigApi.setRefreshToken(refreshToken);
            BigApi.setAccessToken(accessToken);
            const accountProfile = await BigApi.fetchLocalAccountProfile();
            return accountProfile;
        }
        catch (err) {
            const error = BigApiError.getApiError(err, "Login failed! Please try again later.");
            throw error;
        }
    }
    static async loginWithToken(singleUseLoginToken) {
        try {
            const res = await superagent.get(`${BigApi.config.apiServerUrl}/auth/token/login/${singleUseLoginToken}`)
                .set("Authorization", `Bearer ${BigApi.config.apiKey}`)
                .timeout(10000)
                .accept('json');
            const refreshToken = res.headers['x-refresh-token'];
            const accessToken = res.headers['x-access-token'];
            BigApi.setRefreshToken(refreshToken);
            BigApi.setAccessToken(accessToken);
            const accountProfile = await BigApi.fetchLocalAccountProfile();
            return accountProfile;
        }
        catch (e) {
            throw BigApiError.getApiError(e, "Login failed! Please try again later.");
        }
    }
    /**
     * Handle a single use login with a token, returning a url to redirect to if the login was successful.
     * @param singleUseLoginToken
     * @returns
     */
    static async loginWithToken2(singleUseLoginToken) {
        try {
            const res = await superagent.get(`${BigApi.config.apiServerUrl}/auth/token2/login/${singleUseLoginToken}`)
                .set("Authorization", `Bearer ${BigApi.config.apiKey}`)
                .timeout(10000)
                .accept('json');
            const refreshToken = res.headers['x-refresh-token'];
            const accessToken = res.headers['x-access-token'];
            BigApi.setRefreshToken(refreshToken);
            BigApi.setAccessToken(accessToken);
            BigApi.setCookie("retry_token", singleUseLoginToken, 1);
            if (res.body.redirectUrl) {
                return res.body.redirectUrl;
            }
            else {
                return "/";
            }
        }
        catch (e) {
            throw BigApiError.getApiError(e, "Login failed! Please try again later.");
        }
        /*
        // HACK HACK HACK
        //
        // Cache the token for safari users.
        //
        // THIS IS A MASSIVE SECURITY RISK. WIND THIS BACK ONCE WE SHIP A PROPER IOS AND ANDROID APP.
        // TODO: the token2 login MUST BE READONLY.
        const tokenHash = getTokenHash(accessToken);
        redisClient.set(`scanToken.${tokenHash}`, request.params.publicToken);
        redisClient.expire(`scanToken.${tokenHash}`, 60 * 60); // Token expires after 1 hour
        */
    }
    static async createAccount(payload) {
        try {
            const res = await superagent.post(`${BigApi.config.apiServerUrl}/auth/account`)
                .set("Authorization", `Bearer ${BigApi.config.apiKey}`)
                .timeout(10000)
                .accept('json')
                .send(payload);
            const refreshToken = res.headers['x-refresh-token'];
            const accessToken = res.headers['x-access-token'];
            BigApi.setRefreshToken(refreshToken);
            BigApi.setAccessToken(accessToken);
            const accountProfile = await BigApi.fetchLocalAccountProfile();
            return accountProfile;
        }
        catch (err) {
            const error = BigApiError.getApiError(err, "Create account failed! Please try again later.");
            throw error;
        }
    }
    static async logout() {
        try {
            await BigApi.checkAccessTokenStatus();
            const res = await superagent.get(`${BigApi.config.apiServerUrl}/auth/logout`)
                .set("Authorization", `Bearer ${BigApi.config.apiKey}`)
                .set("x-access-token", BigApi.getAccessToken())
                .timeout({ response: 30000 });
        }
        catch (e) {
            console.error(e);
            return null;
        }
        BigApi.deleteTokens();
    }
    static async fetchLocalAccountProfile() {
        try {
            const refreshToken = BigApi.getRefreshToken();
            if (!refreshToken) {
                console.warn("No refresh token was set. Login required");
                return null;
            }
            await BigApi.checkAccessTokenStatus();
            const accessToken = BigApi.getAccessToken();
            const res = await superagent.get(`${BigApi.config.apiServerUrl}/auth/account`)
                .set("Authorization", `Bearer ${BigApi.config.apiKey}`)
                .set("x-access-token", _.isNil(accessToken) ? "renew" : accessToken)
                .accept('json');
            return res.body;
        }
        catch (e) {
            console.error(e);
            return null;
        }
    }
    static async isAuthenticated() {
        try {
            const accessToken = BigApi.getAccessToken();
            if (!accessToken) {
                console.warn("No access token was set. Renewal required.");
                return false;
            }
            return BigApi.checkAccessTokenStatus();
        }
        catch (err) {
            return false;
        }
    }
    static async checkAccessTokenStatus() {
        const accessToken = BigApi.getAccessToken();
        if (!accessToken) {
            throw new BigApiError("No access token was set. Login required.");
        }
        try {
            const res = await superagent.get(`${BigApi.config.apiServerUrl}/auth/verify`)
                .timeout(10000)
                .set("Authorization", `Bearer ${BigApi.config.apiKey}`)
                .set("x-access-token", _.isNil(accessToken) ? "renew" : accessToken)
                .accept('json');
            return true;
        }
        catch (err) {
            const errorPayload = {
                error: {
                    messages: []
                }
            };
            if (err.errno && err.errno === "ENOTFOUND") {
                errorPayload.error.messages = [{ msg: "Couldn't connect to the Bigscreen servers." }];
            }
            else if (err.timeout) {
                errorPayload.error.messages = [{ msg: "Couldn't connect to the Bigscreen servers." }];
            }
            else if (err.response.status === 401) {
                if (err.response.body && err.response.body.code) {
                    const authenticationErrorCode = err.response.body.code;
                    if (authenticationErrorCode === AuthenticationErrorCode.AccessTokenNeedsRenewal) {
                        const nonce = err.response.headers["x-bigscreen-nonce"];
                        if (nonce) {
                            const renewalResult = await BigApi.renewAccessToken(nonce);
                            return renewalResult;
                        }
                        else {
                            throw new Error("NO NONCE FUCK YOU");
                        }
                    }
                }
                errorPayload.error.messages = err.response.body;
            }
            else if (err.response.status === 422) {
                errorPayload.error.messages = err.response.body;
            }
            else {
                errorPayload.error.messages = [{ msg: "Login failed!" }];
            }
            console.error(JSON.stringify(err));
            return false;
        }
    }
    static async renewAccessToken(nonce) {
        const refreshToken = BigApi.getRefreshToken();
        if (!refreshToken) {
            console.warn("No refresh token was set. Login required");
            throw new BigApiError("Refresh token was not set. Login required.");
        }
        try {
            const systemInfo = await BigApi.getSystemInfo();
            const encoder = new TextEncoder();
            const data = encoder.encode(JSON.stringify(systemInfo));
            const systemInfoHeaderBase64 = btoa(String.fromCharCode(...data));
            const res = await superagent.get(`${BigApi.config.apiServerUrl}/auth/renew`)
                .set("Authorization", `Bearer ${BigApi.config.apiKey}`)
                .set("x-bigscreen-nonce", nonce)
                .set("x-refresh-token", refreshToken)
                .set("x-bigscreen-system-info", systemInfoHeaderBase64)
                .accept('json');
            // Update the refresh token if one is sent back - not always guaranteed to get one.
            const newRefreshToken = res.headers['x-refresh-token'];
            const newAccessToken = res.headers['x-access-token'];
            if (!_.isNil(newRefreshToken)) {
                BigApi.setRefreshToken(newRefreshToken);
            }
            BigApi.setAccessToken(newAccessToken);
            console.log("SUCCESS THE TOKEN WAS RENEWED AND THE NEXT REQUEST SHOULD FUCKING WORK.");
            return true;
        }
        catch (e) {
            console.error("Error renewing the access token.");
            console.error(e);
        }
        return false;
    }
    static async get(url, authenticationGuard = BigApiAuthenticationGuard.AuthenticationRequired) {
        try {
            if (authenticationGuard === BigApiAuthenticationGuard.SkipAuthentication) {
                const res = await superagent.get(`${BigApi.config.apiServerUrl}${url}`)
                    .set("Authorization", `Bearer ${BigApi.config.apiKey}`)
                    .accept('json');
                return res;
            }
            else if (authenticationGuard === BigApiAuthenticationGuard.AuthenticationRequired) {
                await BigApi.checkAccessTokenStatus();
                const res = await superagent.get(`${BigApi.config.apiServerUrl}${url}`)
                    .set("Authorization", `Bearer ${BigApi.config.apiKey}`)
                    .set("x-access-token", BigApi.getAccessToken())
                    .accept('json');
                return res;
            }
        }
        catch (err) {
            throw BigApiError.getApiError(err);
        }
    }
    static async post(url, payload, authenticationGuard = BigApiAuthenticationGuard.AuthenticationRequired) {
        try {
            if (authenticationGuard === BigApiAuthenticationGuard.SkipAuthentication) {
                const res = await superagent.post(`${BigApi.config.apiServerUrl}${url}`)
                    .set("Authorization", `Bearer ${BigApi.config.apiKey}`)
                    .send(payload)
                    .accept('json');
                return res;
            }
            else if (authenticationGuard === BigApiAuthenticationGuard.AuthenticationRequired) {
                await BigApi.checkAccessTokenStatus();
                const res = await superagent.post(`${BigApi.config.apiServerUrl}${url}`)
                    .set("Authorization", `Bearer ${BigApi.config.apiKey}`)
                    .set("x-access-token", BigApi.getAccessToken())
                    .send(payload)
                    .accept('json');
                return res;
            }
        }
        catch (err) {
            throw BigApiError.getApiError(err);
        }
    }
    static async put(url, payload) {
        try {
            await BigApi.checkAccessTokenStatus();
            const res = await superagent.put(`${BigApi.config.apiServerUrl}${url}`)
                .set("Authorization", `Bearer ${BigApi.config.apiKey}`)
                .set("x-access-token", BigApi.getAccessToken())
                .send(payload)
                .accept('json');
            return res;
        }
        catch (err) {
            throw BigApiError.getApiError(err);
        }
    }
    static async delete(url) {
        try {
            await BigApi.checkAccessTokenStatus();
            const res = await superagent.del(`${BigApi.config.apiServerUrl}${url}`)
                .set("Authorization", `Bearer ${BigApi.config.apiKey}`)
                .set("x-access-token", BigApi.getAccessToken())
                .accept('json');
            return res;
        }
        catch (err) {
            throw BigApiError.getApiError(err);
        }
    }
    static async getRdcUrl() {
        try {
            const res = await superagent.get(`${BigApi.config.rdcServerHost}/config`);
            return res.body;
        }
        catch (err) {
            throw BigApiError.getApiError(err);
        }
    }
}
